import type { ReactNode } from "react"
import { memo, useEffect, useRef, useState } from "react"
import {
	IconMagnifierOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"

import type { HelpCenterArticle, HelpCenterGroup } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import { IconButton, Input, Tooltip, useBreakpoints } from "@productlane/ui"

import { RoleCheck } from "@/components/shared/role-check"
import { useActivePortalInstanceStore } from "@/stores/active-portal-instance-store"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { PageHeader } from "../page-header"
import { MOBILE_BAR_CLEARANCE } from "../sidebar-geometry"
import { AddArticleButton } from "./add-article-button"
import { DocsTabBar } from "./docs-tab-bar"
import { useHelpCenterNav } from "./help-center-context"
import { HelpCenterNavContent } from "./help-center-nav-content"
import { HelpCenterSearchResults } from "./help-center-search-results"
import { folderKeyForArticle } from "./hidden-tier-folders"
import { SuggestionsSection } from "./suggestions-section"
import { useSelectDocsTab } from "./use-docs-tabs"

const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
	<IconMagnifierOutline18 {...props} strokeWidth={1} />
)

export const HelpCenterNav = memo(
	({
		articleGroups,
		selectedArticle,
		isDetailOpen,
		isHomeActive = false,
		portalSwitcher,
		mobileActions,
	}: {
		articleGroups: readonly (HelpCenterGroup & {
			helpCenterArticles: readonly HelpCenterArticle[]
		})[]
		selectedArticle?: HelpCenterArticle
		// A phone shows the tree or the article, never both.
		isDetailOpen: boolean
		isHomeActive?: boolean
		// Takes the title's place once the workspace has more than one portal.
		portalSwitcher?: ReactNode
		mobileActions?: ReactNode
	}) => {
		const { closeGroup } = useDocNavStore()
		const { isMobile } = useBreakpoints()
		const selectTab = useSelectDocsTab()
		const { onItemSelected } = useHelpCenterNav()
		const [query, setQuery] = useState("")
		const [isSearchOpen, setIsSearchOpen] = useState(false)
		const searchInputRef = useRef<HTMLInputElement>(null)
		const isSearching = query.trim().length > 0

		useEffect(() => {
			if (isSearchOpen) {
				searchInputRef.current?.focus()
			}
		}, [isSearchOpen])

		const closeSearch = () => {
			setQuery("")
			setIsSearchOpen(false)
		}
		// Suggestions are tied to the main (root) portal for now — hide the
		// section when the user has any non-root portal selected.
		const isMainPortal = useActivePortalInstanceStore(
			(s) => s.activePortalInstanceId === null,
		)

		const searchBar = (
			<div
				className={cn(
					"grid transition-[grid-template-rows] duration-200 ease-out",
					isSearchOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				)}
			>
				<div className="overflow-hidden">
					<div className="border-b border-gray-200 px-2.5 py-1 dark:border-gray-700/80">
						<Input
							ref={searchInputRef}
							name="help-center-search"
							variant="borderless"
							inputClassName="h-8 rounded-lg bg-gray-100 !indent-0 pl-7 pr-9 text-sm ring-0 dark:bg-gray-800"
							iconClassName="left-2 text-gray-600 dark:text-gray-300"
							placeholder="Search articles..."
							icon={SearchIcon}
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Escape") {
									closeSearch()
								}
							}}
							suffix={
								<IconButton
									variant="ghost"
									size="sm"
									Icon={IconXmarkOutline18}
									aria-label="Close search"
									onClick={closeSearch}
								/>
							}
						/>
					</div>
				</div>
			</div>
		)

		const searchButton = (
			<Tooltip label="Search articles">
				<IconButton
					variant="ghost"
					Icon={IconMagnifierOutline18}
					aria-label="Search articles"
					onClick={() => (isSearchOpen ? closeSearch() : setIsSearchOpen(true))}
				/>
			</Tooltip>
		)

		const navBody = isSearching ? (
			<HelpCenterSearchResults articleGroups={articleGroups} query={query} />
		) : (
			<>
				{isMainPortal && <SuggestionsSection articleGroups={articleGroups} />}
				<HelpCenterNavContent
					articleGroups={articleGroups}
					isHomeActive={isHomeActive}
				/>
			</>
		)

		return (
			<>
				{!isMobile && (
					<div className="group/sidebar hidden h-full w-64 min-w-64 flex-shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white sm:flex dark:border-gray-700/80 dark:bg-gray-900">
						<PageHeader
							title={portalSwitcher ?? "Docs"}
							clearsNavToggle
							// 17.5px = the pl-2.5 + ml-[7.5px] title offset the other navs use.
							className="h-12 border-b border-gray-200 pr-4 lg:pl-[17.5px] lg:pr-3.5 dark:border-gray-700/80"
							titleClassName="text-primary"
							onTitleClick={
								portalSwitcher
									? undefined
									: () => {
											if (selectedArticle?.helpCenterGroupId) {
												closeGroup(
													folderKeyForArticle(
														selectedArticle,
														selectedArticle.helpCenterGroupId,
													),
												)
											}
											selectTab(null)
											onItemSelected()
										}
							}
						>
							<div className="flex items-center gap-1">
								{searchButton}
								<RoleCheck not={["VIEWER"]}>
									<AddArticleButton
										activeArticleId={selectedArticle?.id}
										articleGroups={articleGroups}
									/>
								</RoleCheck>
							</div>
						</PageHeader>
						{searchBar}
						<div className="no-scrollbar flex-1 overflow-y-auto">{navBody}</div>
					</div>
				)}

				{isMobile && !isDetailOpen && (
					<div className="flex h-full w-full min-w-0 flex-col overflow-hidden bg-white sm:hidden dark:bg-gray-900">
						<PageHeader
							title={portalSwitcher ?? "Docs"}
							className="border-b border-gray-200 pl-4 pr-3 dark:border-gray-700/80"
							titleClassName="text-primary"
						>
							<div className="flex items-center gap-1">
								{mobileActions}
								{searchButton}
								<RoleCheck not={["VIEWER"]}>
									<AddArticleButton
										activeArticleId={selectedArticle?.id}
										articleGroups={articleGroups}
									/>
								</RoleCheck>
							</div>
						</PageHeader>
						{/* The right pane, where the tabs sit on a wider screen, is hidden here. */}
						<div className="flex h-11 flex-none items-center border-b border-gray-200 px-2.5 dark:border-gray-700/80">
							<DocsTabBar articleGroups={articleGroups} />
						</div>
						{searchBar}
						<div className="no-scrollbar min-h-0 w-full flex-1 overflow-y-auto">
							<div className={cn("w-full", MOBILE_BAR_CLEARANCE)}>
								{navBody}
							</div>
						</div>
					</div>
				)}
			</>
		)
	},
)
